import React, { Fragment, useState, useEffect } from 'react';
import { auditorsApi } from '../api';
import { toast } from 'sonner';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import {
  BarChart3,
  Loader2,
  Users,
  FileText,
  ClipboardCheck,
  CheckCircle,
  Calendar,
  Building2,
  MessageSquare,
} from 'lucide-react';

const RESULT_LABELS = {
  conforming: { label: 'Conforme', color: 'badge-green' },
  non_conforming: { label: 'Não Conforme', color: 'badge-red' },
  improvement: { label: 'Melhorar', color: 'badge-yellow' },
  not_applicable: { label: 'N/A', color: 'badge-gray' },
  not_verified: { label: 'Não Verificado', color: 'badge-yellow' },
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('pt-BR');
};

const AuditorReport = () => {
  const [auditors, setAuditors] = useState([]);
  const [selectedAuditorId, setSelectedAuditorId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuditors();
  }, []);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAuditorId]);

  const loadAuditors = async () => {
    try {
      const res = await auditorsApi.listActive();
      setAuditors(res.data || []);
    } catch (error) {
      console.error('Error loading auditors:', error);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedAuditorId) params.auditor_id = selectedAuditorId;
      const res = await auditorsApi.report(params);
      setRows(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar relatório de avaliações');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const grouped = rows.reduce((acc, row) => {
    const name = row.auditor_name || 'Sem auditor';
    if (!acc[name]) acc[name] = [];
    acc[name].push(row);
    return acc;
  }, {});

  const totalAudits = new Set(rows.map(r => r.audit_code).filter(Boolean)).size;
  const totalVistos = rows.filter(r => r.visto).length;

  const ResultBadge = ({ result }) => {
    if (!result) return <span className="badge badge-gray">-</span>;
    const config = RESULT_LABELS[result] || RESULT_LABELS.not_verified;
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const TH_CLASS = 'px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase';
  const COLUMNS = [
    { key: 'auditoria', label: 'Auditoria', locked: true, headerClass: TH_CLASS, render: (row) => (
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-gray-500" />
          <div>
            <p className="text-white text-sm font-medium">{row.audit_code}</p>
            <p className="text-xs text-gray-400">{row.audit_title}</p>
            <p className="text-xs text-gray-500">{formatDate(row.planned_date)}</p>
          </div>
        </div>
      </td>
    ) },
    { key: 'item', label: 'Item', headerClass: TH_CLASS, render: (row) => (
      <td className="px-4 py-3">
        <p className="text-sm text-gray-300 max-w-xs truncate">{row.question}</p>
        {row.audited_subject && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <MessageSquare size={12} />
            {row.audited_subject}
          </p>
        )}
      </td>
    ) },
    { key: 'requisito', label: 'Requisito', headerClass: TH_CLASS, render: (row) => <td className="px-4 py-3 text-sm text-gray-400">{row.requirement || '-'}</td> },
    { key: 'setor', label: 'Setor', headerClass: TH_CLASS, render: (row) => (
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <Building2 size={14} className="text-gray-500" />
          <span className="text-sm text-gray-300">{row.sector_name || '-'}</span>
          {row.sector_code && (
            <span className="badge badge-gray">{row.sector_code}</span>
          )}
        </div>
      </td>
    ) },
    { key: 'datahora', label: 'Data/Hora', headerClass: TH_CLASS, render: (row) => <td className="px-4 py-3 text-sm text-gray-400">{formatDateTime(row.scheduled_at)}</td> },
    { key: 'resultado', label: 'Resultado', headerClass: TH_CLASS, render: (row) => <td className="px-4 py-3"><ResultBadge result={row.result} /></td> },
    { key: 'visto', label: 'Visto', headerClass: TH_CLASS, render: (row) => (
      <td className="px-4 py-3">
        {row.visto ? (
          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
            <CheckCircle size={12} />
            Visto
          </span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs bg-gray-700 text-gray-400 flex items-center gap-1 w-fit">
            <CheckCircle size={12} />
            Pendente
          </span>
        )}
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'auditor-report', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="auditor-report">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="text-green-400" />
            Relatório de Avaliações por Auditor
          </h2>
          <p className="text-gray-400 text-sm">
            Itens e setores avaliados por cada auditor nas auditorias
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButtons
            module="auditor-report"
            filters={selectedAuditorId ? { auditor_id: selectedAuditorId } : {}}
          />
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <select
            value={selectedAuditorId}
            onChange={(e) => setSelectedAuditorId(e.target.value)}
            className="input w-full pl-10"
            data-testid="report-auditor-filter"
          >
            <option value="">Todos os auditores</option>
            {auditors.map(a => (
              <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Users className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Auditores</p>
              <p className="text-2xl font-bold text-blue-400">{Object.keys(grouped).length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <FileText className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Auditorias</p>
              <p className="text-2xl font-bold text-green-400">{totalAudits}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <ClipboardCheck className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Itens Avaliados</p>
              <p className="text-2xl font-bold text-purple-400">{rows.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <CheckCircle className="text-yellow-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Vistos</p>
              <p className="text-2xl font-bold text-yellow-400">{totalVistos}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-green-500" size={32} />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          Nenhuma avaliação encontrada. Atribua um auditor aos setores/itens na Agenda da auditoria.
        </div>
      ) : (
        Object.entries(grouped).map(([auditorName, auditorRows]) => (
          <div key={auditorName} className="card overflow-hidden">
            <div className="px-4 py-3 bg-gray-800/60 border-b border-gray-700 flex items-center gap-2">
              <Users className="text-green-400" size={16} />
              <span className="font-semibold text-white">{auditorName}</span>
              <span className="badge badge-gray ml-auto">{auditorRows.length} avaliação(ões)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    {visibleColumns.map(col => (
                      <th key={col.key} className={col.headerClass || 'px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase'}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {auditorRows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-800/50">
                      {visibleColumns.map(col => (
                        <Fragment key={col.key}>{col.render(row)}</Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AuditorReport;
